import { activeSession, localDay } from '@dovefiniscono/core/focus/progress';
import { useGame } from '@dovefiniscono/core/game/GameProvider';
import { usePreferences } from '@dovefiniscono/core/game/preferences';
import { today as todayISO } from '@dovefiniscono/core/lib/dates';
import { budgetStatuses, moneyContext } from '@dovefiniscono/core/money/summary';
import { planNotifications, planTimer } from '@dovefiniscono/core/notifications/plan';
import type {
  NotificationKind,
  NotificationScope,
  PlannedNotification,
} from '@dovefiniscono/shared';
import { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { useCollection, useSyncState } from '../../lib/data';
import { refreshPush } from '../../lib/push';
import { useUser } from '../../lib/session';
import { BREAK_CHANGED, readBreak, readSettings } from '../focus/device';

/** A change to your data waits this long: one list after a burst of edits, not one per edit. */
const DEBOUNCE_MS = 3000;
/** The planner is told to send the lists again: this device has just subscribed. */
export const PLAN_AGAIN = 'dvf:plan-again';

const uploadedKey = (userId: string, scope: NotificationScope) => `dvf:plan:${scope}:${userId}`;

function uploadedHash(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Forgets what was sent, so that the next list goes out even if unchanged. */
export function forgetUploadedPlans(userId: string): void {
  try {
    for (const scope of ['data', 'timer'] as const)
      localStorage.removeItem(uploadedKey(userId, scope));
  } catch {
    // Private mode: nothing was remembered either.
  }
  window.dispatchEvent(new Event(PLAN_AGAIN));
}

/** FNV-1a: enough to notice that a list changed. */
function hashOf(text: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash = Math.imul(hash ^ text.charCodeAt(i), 0x01000193);
  }
  return (hash >>> 0).toString(16);
}

/** Sends a list only when it differs from the last one that arrived. */
async function upload(userId: string, scope: NotificationScope, items: PlannedNotification[]) {
  const key = uploadedKey(userId, scope);
  const hash = hashOf(JSON.stringify(items));
  if (uploadedHash(key) === hash) return;
  await api.notifications.setPlan({ scope, items });
  try {
    localStorage.setItem(key, hash);
  } catch {
    // Private mode: it goes out again next time, no harm done.
  }
}

/** Keeps the server's list up to date for as long as someone asked for notifications. */
export function NotificationPlanner() {
  const [preferences] = usePreferences();
  const settings = preferences.notifications;
  return settings?.enabled ? <Planner off={settings.off} /> : null;
}

function Planner({ off: kinds }: { off: readonly NotificationKind[] }) {
  const user = useUser();
  const game = useGame();
  const tasks = useCollection('tasks');
  const events = useCollection('events');
  const obligations = useCollection('obligations');
  const invoices = useCollection('invoices');
  const purchases = useCollection('plannedPurchases');
  const budgets = useCollection('budgets');
  const transactions = useCollection('transactions');
  const categories = useCollection('categories');
  const accounts = useCollection('moneyAccounts');
  const sessions = useCollection('focusSessions');
  const { lastSyncedAt } = useSyncState();

  const [today, setToday] = useState(todayISO);
  const [pause, setPause] = useState(readBreak);
  const [round, setRound] = useState(0);

  useEffect(() => {
    void refreshPush().catch(() => {});
    // The list only looks ahead from now: a new day is a new list even if nothing else changes.
    const tick = setInterval(() => setToday(todayISO()), 60_000);
    const onBreak = () => setPause(readBreak());
    const onAgain = () => setRound((value) => value + 1);
    window.addEventListener(BREAK_CHANGED, onBreak);
    window.addEventListener(PLAN_AGAIN, onAgain);
    return () => {
      clearInterval(tick);
      window.removeEventListener(BREAK_CHANGED, onBreak);
      window.removeEventListener(PLAN_AGAIN, onAgain);
    };
  }, []);

  const dayStats = game.days.get(today);
  const activeToday = Boolean(
    dayStats && (dayStats.sessions > 0 || dayStats.tasks > 0 || dayStats.units > 0),
  );

  const dataPlan = useMemo(() => {
    const context = moneyContext(accounts, categories);
    return planNotifications({
      now: new Date(),
      off: kinds,
      tasks,
      events,
      obligations,
      invoices,
      purchases,
      budgets: budgetStatuses(budgets, transactions, today.slice(0, 7), context),
      categoryName: (id) => categories.find((category) => category.id === id)?.name ?? null,
      streak: { days: game.streak, activeToday, isRest: game.isRest },
    });
  }, [
    today,
    kinds,
    tasks,
    events,
    obligations,
    invoices,
    purchases,
    budgets,
    transactions,
    categories,
    accounts,
    game.streak,
    game.isRest,
    activeToday,
  ]);

  const running = activeSession(sessions) ?? null;
  const completedToday = sessions.filter(
    (session) => session.completed && localDay(session.startedAt) === today,
  ).length;
  const timerPlan = useMemo(
    () =>
      planTimer({
        now: new Date(),
        off: kinds,
        running,
        pause,
        settings: readSettings(),
        completedToday,
      }),
    [kinds, running, pause, completedToday],
  );

  // Only after the first sync of this visit: an old copy must not replace a newer list.
  const synced = lastSyncedAt !== null;

  // biome-ignore lint/correctness/useExhaustiveDependencies: each sync and each `round` retries.
  useEffect(() => {
    if (!synced) return;
    const timer = setTimeout(() => {
      void upload(user.id, 'data', dataPlan).catch(() => {});
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [synced, user.id, dataPlan, lastSyncedAt, round]);

  // The timer at once: it may be the last thing done before locking the phone. Only from the
  // device in use: another one does not know about this one's break, and would erase it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: each sync and each `round` retries.
  useEffect(() => {
    if (document.visibilityState !== 'visible') return;
    void upload(user.id, 'timer', timerPlan).catch(() => {});
  }, [user.id, timerPlan, lastSyncedAt, round]);

  return null;
}
